'use client';

import { c as _c } from "react/compiler-runtime";
import { type ItemsRenderProps, useGetCreatedMarketsAndProposalsInfinite } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { PortfolioListTransition } from '@/components/portfolio/positions-card/portfolio-list-transition';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { ChartRoseIconSolid, Rocket01IconSolid } from '@/components/ui/icon/glyphs';
import { InfiniteList } from '@/components/ui/infinite-list';
import { logger } from '@/lib/telemetry/logger';
import { CreatedMarketRow } from './created-market-row';
import { CreatedProposalRow } from './created-proposal-row';
import { useCreatorBackerRewards } from './use-creator-backer-rewards';
export interface CreatedListProps {
  enabled?: boolean;
  userId: string;
  sortOrder: 'asc' | 'desc';
  renderControls: (count: number | undefined, hasMore: boolean) => React.ReactNode;
}
export function CreatedMarkets(t0) {
  const $ = _c(29);
  const {
    userId,
    enabled: t1,
    sortOrder,
    status,
    renderControls
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== sortOrder || $[1] !== status || $[2] !== userId) {
    t2 = {
      creatorUserId: userId,
      group: "markets",
      status,
      sortOrder
    };
    $[0] = sortOrder;
    $[1] = status;
    $[2] = userId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== enabled) {
    t3 = {
      enabled
    };
    $[4] = enabled;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const query = useGetCreatedMarketsAndProposalsInfinite(t2, t3);
  const {
    backerRewardsFor,
    observeBackerRewardsRow
  } = useCreatorBackerRewards(enabled);
  const t4 = query.isPending || query.isError || query.isPlaceholderData ? undefined : query.rows.length;
  let t5;
  if ($[6] !== query.hasNextPage || $[7] !== renderControls || $[8] !== t4) {
    t5 = renderControls(t4, query.hasNextPage);
    $[6] = query.hasNextPage;
    $[7] = renderControls;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `created-markets:${status}`;
  let t7;
  if ($[10] !== backerRewardsFor || $[11] !== observeBackerRewardsRow) {
    t7 = t8 => {
      const {
        id,
        ref
      } = t8;
      return <li key={id} ref={ref} className="mb-4 lg:mb-0"><CreatedMarketRow pubkey={id} backerRewards={backerRewardsFor(id)} backerRewardsRef={observeBackerRewardsRow(id)} /></li>;
    };
    $[10] = backerRewardsFor;
    $[11] = observeBackerRewardsRow;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== query.Items || $[14] !== query.queryKey || $[15] !== t7) {
    t8 = <ul className="flex flex-col"><query.Items queryKey={query.queryKey}>{t7}</query.Items></ul>;
    $[13] = query.Items;
    $[14] = query.queryKey;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== query || $[18] !== t8) {
    t9 = <CreatedListState query={query} group="markets">{t8}</CreatedListState>;
    $[17] = query;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t6 || $[21] !== t9) {
    t10 = <PortfolioListTransition view={t6}>{t9}</PortfolioListTransition>;
    $[20] = t6;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== query.fetchNextPage || $[24] !== t10) {
    t11 = <CreatedListViewport fetchNextPage={query.fetchNextPage}>{t10}</CreatedListViewport>;
    $[23] = query.fetchNextPage;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t5) {
    t12 = <>{t5}{t11}</>;
    $[26] = t11;
    $[27] = t5;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
}
export function CreatedProposals(t0) {
  const $ = _c(26);
  const {
    userId,
    enabled: t1,
    sortOrder,
    renderControls
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== sortOrder || $[1] !== userId) {
    t2 = {
      creatorUserId: userId,
      group: "proposals",
      sortOrder
    };
    $[0] = sortOrder;
    $[1] = userId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== enabled) {
    t3 = {
      enabled
    };
    $[3] = enabled;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const query = useGetCreatedMarketsAndProposalsInfinite(t2, t3);
  let t4;
  if ($[5] !== query.rows) {
    t4 = new Map(query.rows.map(_temp));
    $[5] = query.rows;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const rows = t4;
  const t5 = query.isPending || query.isError || query.isPlaceholderData ? undefined : query.rows.length;
  let t6;
  if ($[7] !== query.hasNextPage || $[8] !== renderControls || $[9] !== t5) {
    t6 = renderControls(t5, query.hasNextPage);
    $[7] = query.hasNextPage;
    $[8] = renderControls;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== rows) {
    t7 = t8 => {
      const {
        id,
        ref
      } = t8;
      return <li key={id} ref={ref} className="mb-4 min-w-0 lg:mb-0"><CreatedProposalRow pubkey={id} snapshot={rows.get(id)} /></li>;
    };
    $[11] = rows;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== query.Items || $[14] !== query.queryKey || $[15] !== t7) {
    t8 = <ul className="flex flex-col"><query.Items queryKey={query.queryKey}>{t7}</query.Items></ul>;
    $[13] = query.Items;
    $[14] = query.queryKey;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== query || $[18] !== t8) {
    t9 = <PortfolioListTransition view="created-proposals"><CreatedListState query={query} group="proposals">{t8}</CreatedListState></PortfolioListTransition>;
    $[17] = query;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== query.fetchNextPage || $[21] !== t9) {
    t10 = <CreatedListViewport fetchNextPage={query.fetchNextPage}>{t9}</CreatedListViewport>;
    $[20] = query.fetchNextPage;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t6) {
    t11 = <>{t6}{t10}</>;
    $[23] = t10;
    $[24] = t6;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
function _temp(row) {
  return [row.pubkey, row];
}
function CreatedListViewport(t0) {
  const $ = _c(3);
  const {
    fetchNextPage,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== fetchNextPage) {
    t1 = <InfiniteList scrollMode="page-below-lg" hasNextPage={false} isFetchingNextPage={false} fetchNextPage={fetchNextPage}>{children}</InfiniteList>;
    $[0] = children;
    $[1] = fetchNextPage;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
interface ListState {
  isPending: boolean;
  isError: boolean;
  rows: readonly object[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  refetch: () => void;
  fetchNextPage: () => Promise<void>;
}
function CreatedListState(t0) {
  const $ = _c(38);
  const {
    query,
    group,
    children
  } = t0;
  const t = useTranslations("profile.created");
  let t1;
  let t2;
  if ($[0] !== group || $[1] !== query.isError) {
    t1 = () => {
      if (query.isError) {
        logger.warn("portfolio.created_query_failed", {
          group
        });
      }
    };
    t2 = [query.isError, group];
    $[0] = group;
    $[1] = query.isError;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== group || $[5] !== query) {
    t3 = async function loadMore() {
      try {
        await query.fetchNextPage();
      } catch {
        logger.warn("portfolio.created_page_failed", {
          group
        });
      }
    };
    $[4] = group;
    $[5] = query;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const loadMore = t3;
  if (query.isPending) {
    let t4;
    if ($[7] !== t) {
      t4 = t("loading");
      $[7] = t;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Skeleton className="h-20 w-full rounded-card" />;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t4) {
      t6 = <output aria-label={t4}>{t5}</output>;
      $[10] = t4;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  if (query.isError) {
    let t4;
    if ($[12] !== t) {
      t4 = t("error");
      $[12] = t;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] !== query) {
      t5 = () => {
        query.refetch();
      };
      $[14] = query;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== t) {
      t6 = t("retry");
      $[16] = t;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== t5 || $[19] !== t6) {
      t7 = <AnimatedButton variant="secondary" onClick={t5}>{t6}</AnimatedButton>;
      $[18] = t5;
      $[19] = t6;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== t4 || $[22] !== t7) {
      t8 = <EmptyState size="compact" title={t4} action={t7} />;
      $[21] = t4;
      $[22] = t7;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    return t8;
  }
  if (query.rows.length === 0) {
    const t4 = group === "markets" ? ChartRoseIconSolid : Rocket01IconSolid;
    let t5;
    if ($[24] !== group || $[25] !== t) {
      t5 = group === "markets" ? t("emptyMarkets") : t("emptyProposals");
      $[24] = group;
      $[25] = t;
      $[26] = t5;
    } else {
      t5 = $[26];
    }
    let t6;
    if ($[27] !== t4 || $[28] !== t5) {
      t6 = <EmptyState size="section" icon={t4} title={t5} />;
      $[27] = t4;
      $[28] = t5;
      $[29] = t6;
    } else {
      t6 = $[29];
    }
    return t6;
  }
  let t4;
  if ($[30] !== loadMore || $[31] !== query.hasNextPage || $[32] !== query.isFetchingNextPage || $[33] !== t) {
    t4 = query.hasNextPage ? <div className="flex justify-center pt-3"><AnimatedButton variant="secondary" disabled={query.isFetchingNextPage} loading={query.isFetchingNextPage} onClick={() => {
        loadMore();
      }}>{query.isFetchingNextPage ? t("loading") : t("loadMore")}</AnimatedButton></div> : null;
    $[30] = loadMore;
    $[31] = query.hasNextPage;
    $[32] = query.isFetchingNextPage;
    $[33] = t;
    $[34] = t4;
  } else {
    t4 = $[34];
  }
  let t5;
  if ($[35] !== children || $[36] !== t4) {
    t5 = <>{children}{t4}</>;
    $[35] = children;
    $[36] = t4;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  return t5;
}